import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ShieldAlert, AlertTriangle, ExternalLink, RefreshCw } from "lucide-react";
import AdminNav from "@/components/AdminNav";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const FLAG_LABEL = {
  honorario_declarado_bajo: "Honorario declarado bajo (<40% referencia)",
  honorario_bajo_cargo_minimo_aplicado: "Se aplicó cargo mínimo (honorario muy bajo)",
  dual_role_user: "Contraparte con doble rol (abogado + mediador)",
  pair_cancellations_90d: "Historial de cancelaciones del mismo par (90d)",
};

function labelFlag(f) {
  const [head] = f.split(":");
  return FLAG_LABEL[head] || f;
}

function severityClass(score) {
  if (score >= 80) return "text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10";
  if (score >= 60) return "text-amber-300 border-amber-400/40 bg-amber-400/10";
  return "text-cyan-300 border-cyan-400/40 bg-cyan-400/10";
}

export default function AdminRiskCases() {
  const [items, setItems] = useState([]);
  const [minScore, setMinScore] = useState(60);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (score = minScore) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/admin/risk-cases?min_score=${score}`);
      setItems(data.items || []);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(minScore); }, [minScore]);

  return (
    <div className="container-app py-10" data-testid="admin-risk-cases">
      <AdminNav />
      <div className="label-eyebrow">Panel administrativo · anti-fuga</div>
      <h1 className="serif text-4xl mt-2 flex items-center gap-2"><ShieldAlert size={28} className="text-amber-300"/> Casos de riesgo (posible fuga de intermediación)</h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-3xl">
        Delegaciones y mediaciones con señales que sugieren un posible acuerdo fuera de plataforma: honorarios anormalmente bajos, doble rol abogado-mediador, cancelaciones repetidas entre las mismas partes.
      </p>

      <div className="mt-6 panel p-4 flex flex-wrap items-center gap-3" data-testid="risk-filters">
        <label className="text-xs flex items-center gap-2">
          Score mínimo:
          <input type="number" min={0} max={100} step={5} value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value || "0", 10))}
            className="field w-24" data-testid="risk-min-score"/>
        </label>
        <button onClick={() => load(minScore)} disabled={loading} className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="risk-reload">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Actualizar
        </button>
        <span className="text-xs text-muted-foreground ml-auto">{items.length} casos</span>
      </div>

      {error && <div className="mt-4 text-sm text-[hsl(var(--destructive))]">{error}</div>}

      {items.length === 0 ? (
        <div className="panel p-10 text-center text-muted-foreground mt-6" data-testid="risk-empty">
          <ShieldAlert className="mx-auto mb-3 text-emerald-300" size={22}/>
          Sin casos con score ≥ {minScore}. Todo tranquilo.
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((c) => (
            <div key={c.id} className="panel p-5" data-testid={`risk-case-${c.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="pill capitalize text-xs">{c.kind === "mediation" ? "Mediación" : "Delegación"}</span>
                    <span className={`pill text-xs border ${severityClass(c.risk_score)}`} data-testid={`risk-score-${c.id}`}>
                      <AlertTriangle size={11} className="mr-1"/>Score {c.risk_score}
                    </span>
                    <span className="pill capitalize text-xs">{c.status}</span>
                  </div>
                  <h3 className="serif text-xl mt-2">{c.title || c.subject}</h3>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Honorario declarado: <strong className="text-foreground">{CLP(c.agreed_fee_clp || c.current_fee_clp || c.fee_clp)}</strong>
                    {typeof c.commission_clp === "number" && <> · Cargo cobrado: <strong className="text-foreground">{CLP(c.commission_clp)}</strong></>}
                    {c.commission_min_applied && <span className="ml-2 pill badge-pending text-[10px]">mínimo aplicado</span>}
                    {c.commission_reference_applied && <span className="ml-2 pill badge-cyan text-[10px]">tarifa referencial</span>}
                  </div>
                </div>
                <Link
                  to={c.kind === "mediation" ? `/app/mediaciones/${c.id}` : `/app/delegaciones/${c.id}`}
                  className="btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`risk-open-${c.id}`}
                >
                  Abrir <ExternalLink size={12}/>
                </Link>
              </div>
              <ul className="mt-3 flex flex-wrap gap-2 text-xs">
                {(c.risk_flags || []).map((f, idx) => (
                  <li key={idx} className="pill text-[11px] border border-amber-400/25 bg-amber-400/5 text-amber-200">
                    {labelFlag(f)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
